import { Button, Code, Container, Group, Paper, Stack, Text, Title } from '@mantine/core';
import { IconAlertTriangle, IconRefresh } from '@tabler/icons-react';
import { isRouteErrorResponse, useRouteError } from 'react-router-dom';

/**
 * Что показывается вместо белого экрана, когда страница не смогла отрисоваться.
 *
 * Появился по конкретному поводу: ошибка порядка хуков роняла экран участника
 * ровно в момент подключения, и человек видел пустую страницу без единого
 * слова. В сборке для production React не печатает ни имени компонента, ни
 * причины — только ссылку с номером ошибки, которая ничего не говорит ни
 * ведущему, ни участнику.
 *
 * Это страховка, а не лечение: она не делает сломанный экран рабочим. Ошибку
 * всё равно чинят в коде, а сюда попадает то, что не поймали. Поэтому здесь
 * два адресата — человеку понятная фраза и предложение перезагрузить, а
 * разработчику в консоль браузера уходит настоящая ошибка целиком.
 */
export function RouteError() {
  const error = useRouteError();

  /*
    В консоль — полностью. Ошибка уже произошла; скрыть её от того, кто будет
    разбираться, значит потерять единственный след.
  */
  console.error('Ошибка отрисовки маршрута:', error);

  const notFound = isRouteErrorResponse(error) && error.status === 404;
  const title = notFound ? 'Страница не найдена' : 'Страница не открылась';
  const message = notFound
    ? 'Проверьте адрес или вернитесь на главную.'
    : 'Это сбой в интерфейсе, а не в ваших данных. Всё, что было сохранено, на месте.';

  /*
    Текст ошибки показывается только вне production. Пользователю он ничего не
    объясняет, а на людях выглядит как сломанная программа; разработчику он
    нужен рядом с экраном, а не только в консоли.
  */
  const detail = import.meta.env.DEV
    ? error instanceof Error
      ? `${error.name}: ${error.message}`
      : String(error)
    : null;

  return (
    <Container size="sm" py={80}>
      <Paper withBorder p="xl" radius="lg">
        <Stack gap="md">
          <Group gap="sm">
            <IconAlertTriangle size={22} color="var(--gold-deep)" />
            <Title order={2}>{title}</Title>
          </Group>

          <Text c="var(--text-muted)">{message}</Text>

          {detail ? (
            <Code block style={{ whiteSpace: 'pre-wrap' }}>
              {detail}
            </Code>
          ) : null}

          <Group gap="sm" mt="xs">
            {/*
              Обычная перезагрузка, а не переход роутером: состояние компонента
              уже повреждено, и восстанавливать его на месте — гадание.
            */}
            <Button
              leftSection={<IconRefresh size={16} />}
              onClick={() => window.location.reload()}
            >
              Обновить страницу
            </Button>
            <Button variant="light" onClick={() => { window.location.href = '/'; }}>
              На главную
            </Button>
          </Group>

          <Text size="xs" c="var(--text-subtle)">
            Если это повторяется, скажите об этом — с адресом страницы и тем, что
            вы делали перед этим.
          </Text>
        </Stack>
      </Paper>
    </Container>
  );
}
